import React, { createContext } from 'react'
import { TreeViewBaseItem } from '@mui/x-tree-view/models'
import { GenNode } from '../../types/carbon/models/GenNode'
import {
  getNodeID,
  canNodeHaveChildren,
  getNodeTitle,
} from '../../utils/nodeTypes'
import {  
  CheckboxState,
} from '../../utils/tree'

export interface TreeItemType {
  id?: string;
  label?: React.ReactNode;
  checkboxEnabled?: boolean;
  checked?: boolean;
  originalNode: GenNode;
}

// Context to share tree item data
export const TreeItemDataContext = createContext<{
  treeData: Map<string, TreeViewBaseItem<TreeItemType>>,
  getCheckboxState?: (node: GenNode) => CheckboxState;
  /** Per-node hover text; wins over showTooltips/label when it returns a value. */
  getNodeTooltip?: (node: GenNode) => React.ReactNode;
  getNodeActions?: (node: GenNode) => React.ReactElement | null;
  onNodeCheckedToggle?: (id: string, node: GenNode, isChecked: boolean) => void,
  showTooltips?: boolean,
  tooltipsHidden?: boolean,
  disabled?: boolean,
}>({
  treeData: new Map(),
})

// Make tree item IDs unique by including parent context
// This prevents duplicate ID errors when Carbon returns codes with same ID across different variables
// treeItemId is a node's item id in the tree, given its parent's item id.
const treeItemId = (node: GenNode, parentId: string): string => {
  const baseID = node.id ? node.id.toString() : getNodeID(node)
  // Include parent context to ensure uniqueness across the tree
  return parentId ? `${parentId}/${baseID}` : baseID
}

export const convertNodesToTreeItems = (nodes: GenNode[], includeLevels = false, level = 0, parentId = ''): TreeViewBaseItem<TreeItemType>[] => {
  return nodes.map(node => {
    const uniqueID = treeItemId(node, parentId)
    const useID = includeLevels ? `${uniqueID}-level${level}` : uniqueID
    const treeNode: TreeViewBaseItem<TreeItemType> = {
      id: useID,
      label: getNodeTitle(node),
      originalNode: node,
    }

    if(canNodeHaveChildren(node)) {
      treeNode.children = convertNodesToTreeItems(node.children || [], includeLevels, level+1, useID)
    }

    return treeNode
  })
}


// initiallyOpenFolderIds is which folders a variable tree starts with open:
// the ones the job's variable tree file marks open ("+" in the VTR, which
// arrives as isExpanded). On Prius that is the top "Source" folder, so its
// sub-folders show on first visit. The Variables page (via useVariableTree)
// and the construct builder's rail both start from this, so they cannot
// drift apart. The ids are the tree's own item ids (convertNodesToTreeItems,
// without levels).
export const initiallyOpenFolderIds = (nodes: GenNode[], parentId = ''): string[] => {
  const result: string[] = []
  for (const node of nodes) {
    const id = treeItemId(node, parentId)
    if (node.type === 'Folder' && node.isExpanded === true && id) result.push(id)
    if (node.children && node.children.length > 0) result.push(...initiallyOpenFolderIds(node.children, id))
  }
  return result
}

export const buildTreeItemDataMap = (items: TreeViewBaseItem<TreeItemType>[], map: Map<string, TreeViewBaseItem<TreeItemType>> = new Map()): Map<string, TreeViewBaseItem<TreeItemType>> => {
  for (const item of items) {
    if (item.id) {
      map.set(item.id, item)
    }
    if (item.children) {
      buildTreeItemDataMap(item.children, map)
    }
  }
  return map
}
